import { FC, useCallback } from 'react'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { Memo } from '@/hoc/Memo'
import useWindowSize from '@/hooks/useWindowSize'
import useBackendError from '@/hooks/useBackendError'
import yup from '@/utils/yup-extended'
import { LoginModel } from '@/core/services/Auth/models/AuthModel'
import { useAppDispatch } from '@/core/store/hooks'
import { loginThunk } from '@/core/services/Auth/store/auth.slice'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { licensesByCompanyCodeThunk } from '../LicensesPage/store/licenses.slice'
import { getItem } from '@/utils/persistanceStorage'

import { Title } from '@/components/Title'
import { FormGroup } from '@/components/form/FormGroup'
import { Field } from '@/components/form/Field'
import { Button } from '@/components/Button'

import './styles/LoginPage.scss'

const cnLogin = cn('Login')

export const LoginPage: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const { search } = useLocation()
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const { width } = useWindowSize()

  const styles = {
    backgroundImage: `url('./img/login.jpg')`,
  }

  const LoginSchema = yup.object().shape({
    username: yup
      .string()
      .trim()
      .email(t('Email is not correct'))
      .required(t('Email is a required field')),
    password: yup
      .string()
      .trim()
      .required(t('Password is a required field'))
      .min(
        8,
        t('The value must be at least characters long', {
          value: 8,
        }),
      )
      .isValidPassword(t('isInvalidPassword')),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginModel>({
    mode: 'onChange',
    resolver: yupResolver(LoginSchema),
  })

  const login = useCallback(
    async (data: LoginModel) => {
      try {
        const res = await dispatch(loginThunk(data)).unwrap()

        if (res.status === 200) {
          dispatch(
            setToast({
              type: ToastType.success,
              message: t('You are successfully logged in'),
              position: 'top-center',
            }),
          )

          const companyCode = getItem('companyCode', false) || null

          if (companyCode) {
            const res = await dispatch(
              licensesByCompanyCodeThunk(companyCode),
            ).unwrap()

            navigate(`/cabinet/licenses/renew?licenseId=${res.id}`)
            localStorage.removeItem('companyCode')
          } else {
            navigate('/cabinet/licenses')
          }
        }
      } catch (errors) {
        setBackendError(errors)
      }
    },
    [search],
  )

  return (
    <>
      <Helmet>
        <title>{t('Login')} | Registration</title>
      </Helmet>
      <div className={cnLogin()}>
        <form className={cnLogin('form')} onSubmit={handleSubmit(login)}>
          <Title className={cnLogin('title')}>{t('Access your account')}</Title>
          <FormGroup>
            <Field
              {...register('username')}
              placeholder={t('Enter email')}
              label={t('Email')}
              errors={errors.username && errors.username.message}
            />
          </FormGroup>
          <FormGroup>
            <Field
              {...register('password')}
              placeholder={t('Enter password')}
              label={t('Password')}
              type='password'
              errors={errors.password && errors.password.message}
            />
          </FormGroup>
          <Button isLoading={isSubmitting} type='submit' fluid>
            {t('Log in')}
          </Button>

          <div className={cnLogin('bottom')}>
            <div className={cnLogin('item')}>
              <div className={cnLogin('desc')}>{t('Forgot password?')}</div>
              <NavLink className={cnLogin('link')} to='/forgot-password'>
                {t('Press Here')}
              </NavLink>
            </div>

            <div className={cnLogin('item')}>
              <div className={cnLogin('desc')}>
                {t('Create a new account?')}
              </div>
              <NavLink className={cnLogin('link')} to='/registration'>
                {t('Join now')}
              </NavLink>
            </div>
          </div>
        </form>

        {width >= 768 && (
          <div className={cnLogin('image')} style={styles}></div>
        )}
      </div>
    </>
  )
})
